import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
    addDoc,
    arrayRemove,
    arrayUnion,
    collection,
    deleteDoc,
    doc,
    getDocs,
    getFirestore,
    orderBy,
    query,
    serverTimestamp,
    updateDoc,
    where,
} from 'firebase/firestore';
import React, { useCallback, useEffect, useState } from 'react';
import { app } from '../firebase';
import { hasPlayableAudio, normalizeTrackDocument } from '../utils/tracks';
import './Playlists.css';

const auth = getAuth(app);
const db = getFirestore(app);

const dateValue = (value) => {
    if (!value) return 0;
    if (typeof value.toMillis === 'function') return value.toMillis();
    if (typeof value.toDate === 'function') return value.toDate().getTime();
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
};

const normalizePlaylist = (snapshotDoc) => {
    const data = snapshotDoc.data();
    return {
        id: snapshotDoc.id,
        ...data,
        tracks: Array.isArray(data.tracks) ? data.tracks : [],
    };
};

const normalizeTrack = normalizeTrackDocument;

const sortByRecent = (a, b) => dateValue(b.updatedAt || b.createdAt || b.uploadedAt) - dateValue(a.updatedAt || a.createdAt || a.uploadedAt);

const formatPrice = (price) => {
    const numericPrice = Number(price);
    return Number.isFinite(numericPrice) ? `$${numericPrice.toFixed(2)}` : '$0.00';
};

const Playlists = () => {
    const [playlists, setPlaylists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newPlaylistName, setNewPlaylistName] = useState('');
    const [selectedPlaylist, setSelectedPlaylist] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [user, setUser] = useState(() => auth.currentUser);
    const [availableTracks, setAvailableTracks] = useState([]);
    const [showAddTrackModal, setShowAddTrackModal] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [playingTrack, setPlayingTrack] = useState(null);
    const [audioRef, setAudioRef] = useState(null);

    const fetchPlaylists = useCallback(async () => {
        if (!user) return;

        setLoading(true);
        setError('');
        try {
            const q = query(collection(db, 'playlists'), where('userId', '==', user.uid));
            const querySnapshot = await getDocs(q);
            const fetchedPlaylists = querySnapshot.docs
                .map(normalizePlaylist)
                .sort(sortByRecent);
            setPlaylists(fetchedPlaylists);
        } catch (err) {
            console.error('Error fetching playlists:', err);
            setError('Failed to load playlists from Firestore.');
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, setUser);
        return unsubscribe;
    }, []);

    useEffect(() => {
        if (user) {
            fetchPlaylists();
        } else {
            setPlaylists([]);
            setLoading(false);
        }
    }, [fetchPlaylists, user]);

    const loadAvailableTracks = async () => {
        setError('');
        try {
            let tracksSnapshot;
            try {
                const tracksQuery = query(collection(db, 'tracks'), orderBy('uploadedAt', 'desc'));
                tracksSnapshot = await getDocs(tracksQuery);
            } catch (orderedError) {
                console.warn('Ordered tracks query failed, loading unsorted tracks:', orderedError);
                tracksSnapshot = await getDocs(collection(db, 'tracks'));
            }

            const allTracks = tracksSnapshot.docs
                .map(normalizeTrack)
                .filter((track) => track.title && track.artist && hasPlayableAudio(track))
                .sort(sortByRecent);

            setAvailableTracks(allTracks);
            setShowAddTrackModal(true);
        } catch (err) {
            console.error('Error fetching tracks:', err);
            setError('Failed to load tracks from Firestore.');
        }
    };

    const handleCreatePlaylist = async (e) => {
        e.preventDefault();
        if (!newPlaylistName.trim() || !user) return;

        setIsCreating(true);
        setError('');
        try {
            const playlistData = {
                name: newPlaylistName.trim(),
                userId: user.uid,
                tracks: [],
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };

            await addDoc(collection(db, 'playlists'), playlistData);

            setNewPlaylistName('');
            setShowCreateModal(false);
            fetchPlaylists();
        } catch (err) {
            console.error('Error creating playlist:', err);
            setError('Failed to create playlist. Please try again.');
        } finally {
            setIsCreating(false);
        }
    };

    const handleDeletePlaylist = async (playlistId) => {
        if (!window.confirm('Are you sure you want to delete this playlist?')) return;

        try {
            await deleteDoc(doc(db, 'playlists', playlistId));
            fetchPlaylists();
        } catch (err) {
            console.error('Error deleting playlist:', err);
            setError('Failed to delete playlist.');
        }
    };

    const getPlaylistCover = (playlist) => {
        if (playlist.coverImage) return playlist.coverImage;
        if (playlist.tracks && playlist.tracks.length > 0 && playlist.tracks[0].coverImage) {
            return playlist.tracks[0].coverImage;
        }
        return null;
    };

    return (
        <div className="playlists-page">
            <div className="container">
                {/* Header */}
                <div className="playlists-header">
                    <div>
                        <h1 className="page-title">
                            Your <span className="gradient-text">Playlists</span>
                        </h1>
                        <p className="page-subtitle">
                            Organize your favorite tracks into playlists
                        </p>
                    </div>
                    {user && (
                        <button
                            className="create-playlist-button"
                            onClick={() => setShowCreateModal(true)}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="12" y1="5" x2="12" y2="19"/>
                                <line x1="5" y1="12" x2="19" y2="12"/>
                            </svg>
                            <span>Create Playlist</span>
                        </button>
                    )}
                </div>

                {/* Auth Warning */}
                {!user && (
                    <div className="auth-warning">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                            <line x1="12" y1="9" x2="12" y2="13"/>
                            <line x1="12" y1="17" x2="12.01" y2="17"/>
                        </svg>
                        <p>Please log in to view and create playlists.</p>
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div className="message error-message">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="12" y1="8" x2="12" y2="12"/>
                            <line x1="12" y1="16" x2="12.01" y2="16"/>
                        </svg>
                        <span>{error}</span>
                    </div>
                )}

                {/* Loading State */}
                {loading && (
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <p>Loading playlists...</p>
                    </div>
                )}

                {/* Playlists Grid */}
                {!loading && user && (
                    <>
                        {playlists.length === 0 ? (
                            <div className="empty-state">
                                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                    <path d="M9 18V5l12-2v13"/>
                                    <circle cx="6" cy="18" r="3"/>
                                    <circle cx="18" cy="16" r="3"/>
                                </svg>
                                <h3>No playlists yet</h3>
                                <p>Create your first playlist to get started</p>
                                <button
                                    className="create-first-button"
                                    onClick={() => setShowCreateModal(true)}
                                >
                Create Playlist
            </button>
                            </div>
                        ) : (
            <div className="playlists-grid">
                                {playlists.map((playlist) => {
                                    const coverImage = getPlaylistCover(playlist);
                                    const trackCount = playlist.tracks ? playlist.tracks.length : 0;
                                    
                                    return (
                    <div key={playlist.id} className="playlist-card">
                                            <div className="playlist-cover">
                                                {coverImage ? (
                                                    <img src={coverImage} alt={playlist.name} />
                                                ) : (
                                                    <div className="playlist-cover-placeholder">
                                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M9 18V5l12-2v13"/>
                                                            <circle cx="6" cy="18" r="3"/>
                                                            <circle cx="18" cy="16" r="3"/>
                                                        </svg>
                                                    </div>
                                                )}
                                                <div className="playlist-overlay">
                                                    <button
                                                        className="playlist-play-btn"
                                                        onClick={() => setSelectedPlaylist(playlist)}
                                                    >
                                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                                                            <path d="M8 5v14l11-7z"/>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="playlist-info">
                                                <h3 className="playlist-name">{playlist.name}</h3>
                                                <p className="playlist-meta">
                                                    {trackCount} {trackCount === 1 ? 'track' : 'tracks'}
                                                </p>
                                            </div>
                                            <div className="playlist-actions">
                                                <button
                                                    className="action-btn"
                                                    onClick={() => setSelectedPlaylist(playlist)}
                                                    title="View playlist"
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                                        <circle cx="12" cy="12" r="3"/>
                                                    </svg>
                                                </button>
                                                <button
                                                    className="action-btn delete-btn"
                                                    onClick={() => handleDeletePlaylist(playlist.id)}
                                                    title="Delete playlist"
                                                >
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <polyline points="3 6 5 6 21 6"/>
                                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}

                {/* Create Playlist Modal */}
                {showCreateModal && (
                    <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Create New Playlist</h2>
                                <button
                                    className="modal-close"
                                    onClick={() => {
                                        setShowCreateModal(false);
                                        setNewPlaylistName('');
                                    }}
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                </button>
                            </div>
                            <form onSubmit={handleCreatePlaylist} className="modal-form">
                                <div className="form-group">
                                    <label htmlFor="playlist-name" className="form-label">
                                        Playlist Name <span className="required">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        id="playlist-name"
                                        value={newPlaylistName}
                                        onChange={(e) => setNewPlaylistName(e.target.value)}
                                        placeholder="Enter playlist name"
                                        required
                                        className="form-input"
                                        autoFocus
                                    />
                                </div>
                                <div className="modal-actions">
                                    <button
                                        type="button"
                                        className="cancel-button"
                                        onClick={() => {
                                            setShowCreateModal(false);
                                            setNewPlaylistName('');
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="submit-button"
                                        disabled={isCreating || !newPlaylistName.trim()}
                                    >
                                        {isCreating ? 'Creating...' : 'Create'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Playlist Detail Modal */}
                {selectedPlaylist && (
                    <div className="modal-overlay" onClick={() => setSelectedPlaylist(null)}>
                        <div className="modal-content playlist-detail-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>{selectedPlaylist.name}</h2>
                                <button
                                    className="modal-close"
                                    onClick={() => setSelectedPlaylist(null)}
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                </button>
                            </div>
                            <div className="playlist-detail-content">
                                <div className="playlist-detail-header">
                                    <div className="playlist-stats">
                                        <span>{selectedPlaylist.tracks?.length || 0} tracks</span>
                                        <span>•</span>
                                        <span>{selectedPlaylist.tracks?.reduce((acc, t) => acc + (t.duration || 0), 0) || 0} min</span>
                                    </div>
                                    <button
                                        className="add-track-to-playlist-btn"
                                        onClick={loadAvailableTracks}
                                    >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <line x1="12" y1="5" x2="12" y2="19"/>
                                            <line x1="5" y1="12" x2="19" y2="12"/>
                                        </svg>
                                        Add Tracks
                                    </button>
                                </div>

                                {selectedPlaylist.tracks && selectedPlaylist.tracks.length > 0 ? (
                                    <div className="playlist-tracks-list">
                                        {selectedPlaylist.tracks.map((track, index) => (
                                            <div key={track.id || index} className="playlist-track-item">
                                                <div className="track-number">{index + 1}</div>
                                                <div className="track-cover-small">
                                                    {track.coverImage ? (
                                                        <img src={track.coverImage} alt={track.title || track.name} />
                                                    ) : (
                                                        <div className="track-cover-placeholder-small"></div>
                                                    )}
                                                    {track.fileUrl && (
                                                        <button
                                                            className="track-play-small"
                                                            onClick={() => {
                                                                if (playingTrack === track.id) {
                                                                    if (audioRef) {
                                                                        audioRef.pause();
                                                                        setPlayingTrack(null);
                                                                    }
                                                                } else {
                                                                    if (audioRef) audioRef.pause();
                                                                    const newAudio = new Audio(track.fileUrl);
                                                                    newAudio.play();
                                                                    setPlayingTrack(track.id);
                                                                    setAudioRef(newAudio);
                                                                    newAudio.onended = () => setPlayingTrack(null);
                                                                }
                                                            }}
                                                        >
                                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                                                {playingTrack === track.id ? (
                                                                    <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                                                                ) : (
                                                                    <path d="M8 5v14l11-7z"/>
                                                                )}
                                                            </svg>
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="track-info">
                                                    <h4>{track.title || track.name || 'Untitled'}</h4>
                                                    <p>{track.artist || 'Unknown Artist'}</p>
                                                    {track.genre && (
                                                        <span className="track-genre-small">{track.genre}</span>
                                                    )}
                                                </div>
                                                <div className="track-actions-playlist">
                                                    {track.price && (
                                                        <span className="track-price-small">{formatPrice(track.price)}</span>
                                                    )}
                                                    <button
                                                        className="remove-track-btn"
                                                        onClick={async () => {
                                                            try {
                                                                const playlistRef = doc(db, 'playlists', selectedPlaylist.id);
                                                                await updateDoc(playlistRef, {
                                                                    tracks: arrayRemove(track),
                                                                    updatedAt: serverTimestamp()
                                                                });
                                                                const updatedPlaylist = {
                                                                    ...selectedPlaylist,
                                                                    tracks: selectedPlaylist.tracks.filter(t => t.id !== track.id)
                                                                };
                                                                setSelectedPlaylist(updatedPlaylist);
                                                                fetchPlaylists();
                                                            } catch (err) {
                                                                console.error('Error removing track:', err);
                                                                setError('Failed to remove track.');
                                                            }
                                                        }}
                                                        title="Remove from playlist"
                                                    >
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <line x1="18" y1="6" x2="6" y2="18"/>
                                                            <line x1="6" y1="6" x2="18" y2="18"/>
                                                        </svg>
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="empty-playlist">
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <path d="M9 18V5l12-2v13"/>
                                            <circle cx="6" cy="18" r="3"/>
                                            <circle cx="18" cy="16" r="3"/>
                                        </svg>
                                        <p>This playlist is empty</p>
                                        <p className="hint">Add tracks from the marketplace</p>
                                        <button
                                            className="add-first-track-btn"
                                            onClick={loadAvailableTracks}
                                        >
                                            Add Tracks
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Add Tracks Modal */}
                {showAddTrackModal && selectedPlaylist && (
                    <div className="modal-overlay" onClick={() => setShowAddTrackModal(false)}>
                        <div className="modal-content add-tracks-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>Add Tracks to {selectedPlaylist.name}</h2>
                                <button
                                    className="modal-close"
                                    onClick={() => {
                                        setShowAddTrackModal(false);
                                        setSearchQuery('');
                                    }}
                                >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                </button>
                            </div>
                            <div className="add-tracks-content">
                                <div className="search-tracks">
                                    <input
                                        type="text"
                                        className="search-input"
                                        placeholder="Search tracks..."
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                                <div className="available-tracks-list">
                                    {availableTracks
                                        .filter(track => 
                                            !selectedPlaylist.tracks?.some(t => t.id === track.id) &&
                                            (searchQuery === '' || 
                                             track.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                             track.artist?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                             track.genre?.toLowerCase().includes(searchQuery.toLowerCase()))
                                        )
                                        .map((track) => (
                                            <div key={track.id} className="available-track-item">
                                                <div className="track-cover-small">
                                                    {track.coverImage ? (
                                                        <img src={track.coverImage} alt={track.title} />
                                                    ) : (
                                                        <div className="track-cover-placeholder-small"></div>
                                                    )}
                                                </div>
                                                <div className="track-info">
                                                    <h4>{track.title || 'Untitled'}</h4>
                                                    <p>{track.artist || 'Unknown Artist'}</p>
                                                    {track.genre && (
                                                        <span className="track-genre-small">{track.genre}</span>
                                                    )}
                                                </div>
                                                <div className="track-actions-add">
                                                    {track.price && (
                                                        <span className="track-price-small">{formatPrice(track.price)}</span>
                                                    )}
                                                    <button
                                                        className="add-track-btn"
                                                        onClick={async () => {
                                                            try {
                                                                const playlistRef = doc(db, 'playlists', selectedPlaylist.id);
                                                                await updateDoc(playlistRef, {
                                                                    tracks: arrayUnion(track),
                                                                    updatedAt: serverTimestamp()
                                                                });
                                                                const updatedPlaylist = {
                                                                    ...selectedPlaylist,
                                                                    tracks: [...(selectedPlaylist.tracks || []), track]
                                                                };
                                                                setSelectedPlaylist(updatedPlaylist);
                                                                fetchPlaylists();
                                                            } catch (err) {
                                                                console.error('Error adding track:', err);
                                                                setError('Failed to add track.');
                                                            }
                                                        }}
                                                    >
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <line x1="12" y1="5" x2="12" y2="19"/>
                                                            <line x1="5" y1="12" x2="19" y2="12"/>
                                                        </svg>
                                                        Add
                                                    </button>
                                                </div>
                    </div>
                ))}
                                    {availableTracks.filter(track => 
                                        !selectedPlaylist.tracks?.some(t => t.id === track.id) &&
                                        (searchQuery === '' || 
                                         track.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                         track.artist?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                                         track.genre?.toLowerCase().includes(searchQuery.toLowerCase()))
                                    ).length === 0 && (
                                        <div className="no-tracks-found">
                                            <p>No tracks available to add</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Playlists;
